import { Field, FieldProps } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { StarEmptyIcon, StarIcon } from '@treams/treams-icons';

import { Paragraph } from '../../common/Text';
import { FieldErrorMessage } from '../errors/FieldErrorMessage';

interface IProps {
	name: string;
	numberOfStars?: number;
	ratingLabelKey?: string;
}

interface IElementProps {
	name: string;
	value?: number;
	numberOfStars?: number;
	ratingLabelKey?: string;
	disabled?: boolean;

	// Returns the numeric value of stars (counts from 1)
	onChange?: (value: number) => void;
}

const Container = styled.div`
	margin-top: -4px;
`;

const Star = styled.button`
	background-color: transparent;
	display: inline-block;
	margin-right: 2px;

	${(props) =>
		props.disabled
			? `
		cursor: default;
	`
			: ''}
	svg {
		width: 20px;
		height: 20px;
		color: ${(props) => props.theme.colors.yellow};
	}
`;

const StarText = styled(Paragraph)`
	display: inline-block;
	margin-left: 10px;
	transform: translateY(-5px);
`;

export const StarRatingElement = ({
	name,
	value,
	onChange,
	disabled,
	numberOfStars = 5,
	ratingLabelKey = 'common.fields.starRating',
}: IElementProps) => {
	const [selectedValue, setSelectedValue] = useState<number | null>(value ?? null);
	const [hoverValue, setHoverValue] = useState<number | null>(null);
	const { t } = useTranslation();

	useEffect(() => {
		if (selectedValue != null && onChange != null) {
			onChange(selectedValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedValue]);

	const activeIndex = (hoverValue != null ? hoverValue : selectedValue) ?? null;

	return (
		<Container
			onMouseLeave={() => {
				setHoverValue(null);
			}}
		>
			{Array(numberOfStars)
				.fill(null)
				.map((_, index) => {
					// Star values are starting from 1, not from 0
					const adjustedIndex = index + 1;

					// Please only use the variable `adjustedIndex` below, do not refer to
					// `index` directly
					const isActive = activeIndex != null && activeIndex >= adjustedIndex;

					return (
						<Star
							key={`${name}-${adjustedIndex}`}
							disabled={disabled}
							type="button"
							onClick={() => {
								if (disabled !== true) {
									setSelectedValue(adjustedIndex);
								}
							}}
							onMouseEnter={() => {
								if (disabled !== true) {
									setHoverValue(adjustedIndex);
								}
							}}
						>
							{isActive ? <StarIcon /> : <StarEmptyIcon />}
						</Star>
					);
				})}

			{activeIndex != null && (
				<StarText color="slateBlue">{t(`${ratingLabelKey}.${activeIndex}`)}</StarText>
			)}
		</Container>
	);
};

export function StarRating({ name, numberOfStars, ratingLabelKey }: IProps) {
	return (
		<>
			<Field name={name}>
				{({ field: { value }, form: { setFieldValue } }: FieldProps<number>) => (
					<StarRatingElement
						name={name}
						onChange={(changedValue) => {
							setFieldValue(name, changedValue);
						}}
						ratingLabelKey={ratingLabelKey}
						numberOfStars={numberOfStars}
						value={value}
					/>
				)}
			</Field>

			<FieldErrorMessage name={name} />
		</>
	);
}
